import { useState, Fragment } from 'react';
import { type PairingV1 as Pairing } from '@hungryroot/rest';
import { type UseCartResult } from '../../../../components/cart/hooks/useCart/useCart';
import { AddToCartModal } from '../../../my-hungryroot/components/AddToCartModal/AddToCartModal';
import {
  AddRemoveAction,
  useAddToCartModal,
} from '../../../my-hungryroot/components/AddToCartModal/AddToCartModalHook';
import { type AppState } from '../../schema';
export enum VisualVariant {
  Dark,
  Light,
}

interface ItemToBeAddedProps {
  points: number;
  id: number;
}

type AddOnlyButtonProps = {
  visualVariant?: VisualVariant;
  onAddClick: () => void;
  onPairingAdded?: () => void;
  disabled?: boolean;
  itemToBeAdded: ItemToBeAddedProps;
  onClick?: () => void;
};

interface PointsContainerProps {
  isLight: boolean;
  itemToBeAdded: ItemToBeAddedProps;
}

function PointsContainer(props: PointsContainerProps) {
  const { isLight, itemToBeAdded } = props;
  return (
    <div className="points-container">
      {/* per conversation https://hungryroot.atlassian.net/browse/WEB-134,
    since this page will be refactored, we're leaving it out of the coin
    icon updates */}
      <img
        className="currency-icon"
        src={`/public/img/currency_icon${isLight ? '_white' : ''}.svg`}
        alt=""
      />
      <span data-testid={`add-button-${itemToBeAdded.id}-points`}>
        {itemToBeAdded?.points}
      </span>
    </div>
  );
}

interface ButtonWithPointsProps {
  disabled?: boolean;
  onClick?: () => void;
  'data-testid': string;
  label: string;
}

export function ButtonWithPoints(
  props: PointsContainerProps & ButtonWithPointsProps
) {
  const { isLight, itemToBeAdded, disabled, onClick, label } = props;
  return (
    <button
      data-testid={props['data-testid']}
      disabled={disabled}
      onClick={onClick}
      className="add-only-button"
    >
      <div className={`add-text ${isLight ? 'recipe' : 'sku'}`}>{label}</div>
      <span className="divider-break"></span>
      <PointsContainer
        isLight={isLight}
        itemToBeAdded={itemToBeAdded}
      ></PointsContainer>
    </button>
  );
}

function AddOnlyButton(props: AddOnlyButtonProps) {
  const { visualVariant, disabled, itemToBeAdded, onClick } = props;

  const { id: itemToBeAddedId } = itemToBeAdded;
  const isLight = visualVariant === VisualVariant.Light;

  return (
    <Fragment key="add-button">
      <ButtonWithPoints
        data-testid={`add-button-${itemToBeAddedId}`}
        isLight={isLight}
        itemToBeAdded={itemToBeAdded}
        onClick={onClick}
        disabled={disabled}
        label="Add"
      />
    </Fragment>
  );
}

type UpdateOnlyButtonProps = {
  volumeLimit: UseCartResult['volumeLimit'];
  boxVolume: UseCartResult['cart']['volume'];
  savedRdpPairing: {
    count: number;
    pairing: NonNullable<AppState['featured']['savedRdpPairing']>;
  };
  points?: string;
  onUpdateClick: () => void;
  itemToBeAdded: ItemToBeAddedProps;
};

function UpdateOnlyButton(props: UpdateOnlyButtonProps) {
  const {
    onUpdateClick,
    volumeLimit,
    boxVolume,
    savedRdpPairing,
    itemToBeAdded,
    points,
  } = props;

  const [showModal, setShowModal] = useState(false);
  const useAddToCartModalQuery = useAddToCartModal();
  const {
    isActive: isAddToCartModalActive,
    accountStatus,
    orderId,
  } = useAddToCartModalQuery;
  const { points: itemToBeAddedPoints, id: itemToBeAddedId } = itemToBeAdded;

  const { pairing: swapPairing, count: savedRdpPairingCartCount } =
    savedRdpPairing;
  const { points: savedRdpPairingPoints } = swapPairing;
  const numOfPoints =
    (parseInt(itemToBeAddedPoints + '', 10) - savedRdpPairingPoints) *
    savedRdpPairingCartCount;

  const isNotAddable = () => {
    return numOfPoints + boxVolume >= volumeLimit;
  };

  return (
    <Fragment key="update-button">
      <button
        data-testid={`update-button-${itemToBeAddedId}`}
        className="update-only-button"
        onClick={() => {
          if (isAddToCartModalActive) {
            setShowModal(true);
          } else {
            onUpdateClick();
          }
        }}
        disabled={isNotAddable()}
      >
        <div className="add-text recipe">Update Recipe</div>
        <span className="divider-break"></span>
        <div className="points-container">
          <img
            className="currency-icon"
            src="/public/img/currency_icon_white.svg"
            alt=""
          />
          <span data-testid={`update-button-${itemToBeAddedId}-points`}>
            {points === undefined ? numOfPoints : points}
          </span>
        </div>
      </button>
      {showModal && (
        <AddToCartModal
          testid="update-button-add-to-cart-modal"
          initialShowModal={showModal}
          accountStatus={accountStatus}
          orderId={orderId}
          onAccountChangeSuccess={onUpdateClick}
          onCancel={() => {
            setShowModal(false);
          }}
        />
      )}
    </Fragment>
  );
}

export enum PlacementVariant {
  Cart,
  Modal,
}

interface AddRemoveInnerProps {
  disabled?: boolean;
  onAddClick: () => void;
  onRemoveClick: () => void;
  onPairingAdded?: () => void;
  onPairingRemoved?: () => void;
  count: number;
  placementVariant?: PlacementVariant;
  points?: string;
  itemToBeAdded: ItemToBeAddedProps;
}

function AddRemoveInner(props: AddRemoveInnerProps) {
  const {
    onAddClick,
    onRemoveClick,
    onPairingAdded,
    onPairingRemoved,
    disabled,
    count,
    itemToBeAdded,
    points,
    placementVariant,
  } = props;

  const [onClickAction, setOnClickAction] = useState<AddRemoveAction | null>(
    null
  );
  const useAddToCartModalQuery = useAddToCartModal();
  const {
    isActive: isAddToCartModalActive,
    accountStatus,
    orderId,
  } = useAddToCartModalQuery;

  const { points: itemToBeAddedPoints, id: itemToBeAddedId } = itemToBeAdded;
  const isCart = placementVariant === PlacementVariant.Cart;
  const renderMinusBtn = () => {
    const buttonTestId = `add-remove-button-remove-${itemToBeAdded.id}`;
    if (count === 1) {
      return (
        <button
          data-testid={buttonTestId}
          className={`minus-btn trash-can ${
            isCart
              ? 'common-cart-add-remove-btn'
              : 'common-outside-add-remove-btn'
          }`}
          onClick={() => {
            if (isAddToCartModalActive) {
              setOnClickAction(AddRemoveAction.Remove);
            } else {
              onRemoveClick();
              onPairingRemoved && onPairingRemoved();
            }
          }}
        >
          <span></span>
        </button>
      );
    } else {
      return (
        <button
          data-testid={buttonTestId}
          className={`minus-btn non-trash-can ${
            isCart
              ? 'common-cart-add-remove-btn'
              : 'common-outside-add-remove-btn'
          }`}
          onClick={() => {
            if (isAddToCartModalActive) {
              setOnClickAction(AddRemoveAction.Remove);
            } else {
              onRemoveClick();
            }
            if (onPairingRemoved) {
              onPairingRemoved();
            }
          }}
        ></button>
      );
    }
  };

  return (
    <div
      className={`add-remove-button ${
        isCart ? `cart-common-cart-add-remove-btn` : 'cart-common-outside'
      }`}
    >
      {renderMinusBtn()}
      <span
        data-testid={`cart-recipe-${itemToBeAddedId}-quantity`}
        className="item-qty hr-demi"
      >
        {count}
      </span>
      <button
        data-testid={`add-remove-button-add-${itemToBeAddedId}`}
        className={`plus-btn ${
          isCart
            ? 'common-cart-add-remove-btn'
            : 'common-outside-add-remove-btn'
        }`}
        onClick={() => {
          if (isAddToCartModalActive) {
            setOnClickAction(AddRemoveAction.Add);
          } else {
            onAddClick();
            onPairingAdded && onPairingAdded();
          }
        }}
        disabled={disabled}
      ></button>
      <div className="points-container hr-subtitle-md-bold">
        {' '}
        <img
          className="currency-icon"
          src={`/public/img/currency_icon${!isCart ? '_white' : ''}.svg`}
          alt=""
        />{' '}
        <span data-testid={`add-button-${itemToBeAddedId}-points`}>
          {points === undefined ? itemToBeAddedPoints : parseFloat(points)}
        </span>
      </div>
      {onClickAction !== null && (
        <AddToCartModal
          testid="add-button-add-to-cart-modal"
          initialShowModal={onClickAction !== null}
          accountStatus={accountStatus}
          orderId={orderId}
          onAccountChangeSuccess={() => {
            if (onClickAction === AddRemoveAction.Add) {
              onAddClick();
              onPairingAdded && onPairingAdded();
            } else {
              onRemoveClick();
              onPairingRemoved && onPairingRemoved();
            }
          }}
          onCancel={() => {
            setOnClickAction(null);
          }}
        />
      )}
    </div>
  );
}

export enum Variant {
  Update,
  AddRemove,
}

interface UpdateProps {
  variant: Variant.Update;
  swapRecipe: {
    count: number;
    pairing: Pairing;
  };
  onUpdateClick: () => void;
}

interface AddRemoveProps {
  variant: Variant.AddRemove;
  onAddClick: () => void;
  onRemoveClick: () => void;
  onPairingAdded?: () => void;
  onPairingRemoved?: () => void;
}

type Props = {
  pairingPoints?: string;
  useCartResult: {
    cart: {
      volume: UseCartResult['cart']['volume'];
    };
    volumeLimit: UseCartResult['volumeLimit'];
  };
  visualVariant?: VisualVariant;
  placementVariant?: PlacementVariant;
  count: number;
  disabled?: boolean;
  onClick?: () => void;
  item: {
    id: number;
    points: string;
  };
} & (
  | UpdateProps
  | AddRemoveProps
  | {
      variant?: undefined;
      onAddClick: () => void;
      onRemoveClick: () => void;
      onPairingAdded?: () => void;
      onPairingRemoved?: () => void;
    }
);

export function AddRemoveBtn(props: Props) {
  const {
    pairingPoints,
    useCartResult,
    visualVariant,
    item,
    variant,
    count,
    disabled,
    placementVariant,
  } = props;

  const {
    cart: { volume: boxVolume },
    volumeLimit,
  } = useCartResult;

  const inCart = count > 0;

  const formattedItem = {
    id: item.id,
    points: parseFloat(item.points),
  };

  if (variant === Variant.Update) {
    return (
      <div className="add-remove-btn-container">
        <UpdateOnlyButton
          points={pairingPoints}
          boxVolume={boxVolume}
          volumeLimit={volumeLimit}
          onUpdateClick={props.onUpdateClick}
          itemToBeAdded={formattedItem}
          savedRdpPairing={props.swapRecipe}
        />
      </div>
    );
  }

  const {
    onAddClick,
    onRemoveClick,
    onPairingAdded,
    onPairingRemoved,
    onClick,
  } = props;

  return (
    <div className="add-remove-btn-container">
      {inCart ? (
        <AddRemoveInner
          itemToBeAdded={formattedItem}
          placementVariant={placementVariant}
          onRemoveClick={onRemoveClick}
          disabled={disabled}
          points={pairingPoints}
          onAddClick={onAddClick}
          onPairingAdded={onPairingAdded}
          onPairingRemoved={onPairingRemoved}
          count={count}
        />
      ) : (
        <AddOnlyButton
          disabled={disabled}
          onAddClick={onAddClick}
          onPairingAdded={onPairingAdded}
          itemToBeAdded={formattedItem}
          visualVariant={visualVariant}
          onClick={onClick}
        />
      )}
    </div>
  );
}
